/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import Icon from "@mui/material/Icon";
import LoopIcon from "@mui/icons-material/Loop";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";

// Settings page components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";

// import BasicInfo from "./components/BasicInfo";
// import ChangePassword from "./components/ChangePassword";
// import DeleteAccount from "./components/DeleteAccount";

// react-router components

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import csvDownload from "json-to-csv-export";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

function ExportManager({ dataSet, forceToggle }) {
  const abortController = new AbortController();

  const [filters, setFilters] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [sportSelected, setSportSelected] = useState(null);
  const [providerSelected, setProviderSelected] = useState(null);
  const [typeSelected, setTypeSelected] = useState({ value: 0, label: "All" });
  const [applyOn, setApplyOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [sports, setSports] = useState([]);
  const [types, setTypes] = useState([
    { value: 3, label: "All" },
    { value: 1, label: "inplay" },
    { value: 2, label: "pregame" },
  ]);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");

  const [notification, setNotification] = useState(false);

  const userData = getUserData();
  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  async function fetchProviders() {
    let user_id = null;
    if (userData) user_id = userData.id;

    if (!user_id) return;

    let postSportsEndpoint = `/api/v1/entities/availableproviders`;
    let response = await getData(
      postSportsEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      let providersData = [];
      providersData = providersData.concat(
        response.data.map((item) => {
          return { value: item.provider_id, label: item.name };
        })
      );
      setProviders(providersData);
    }
  }

  async function fetchSports(provider) {
    let user_id = null;
    let provider_id = null;
    if (userData) user_id = userData.id;
    if (provider) provider_id = provider;

    if (!user_id || !provider_id) return;

    let postSportsEndpoint = `/api/v1/entities/availablesports`;
    let response = await postData(
      postSportsEndpoint,
      { provider_id: provider_id },
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      let sportsData = [{ value: 0, label: "All" }];
      sportsData = sportsData.concat(
        response.data.map((item) => {
          return { value: item.id, label: item.name };
        })
      );
      setSports(sportsData);
    }
  }

  const handleSelectionValueChange = (item, type) => {
    let filtersToSet = { ...filters };

    if (type == "sport-filter") {
      //console.log("sport-filter: ", item);
      item.value = item.map((item) => {
        return item.value;
      });
      setSportSelected(item);
    }

    if (type == "provider-filter") {
      if (dataSet !== "regions") {
        setSportSelected(null);
        setSports([]);
        filtersToSet["sport-filter"] = [];
        //console.log("provider-filter: ", item);

        setProviderSelected(item);
        fetchSports(item.value);
      } else {
        setProviderSelected(item);
      }
    }

    if (type == "type-filter") {
      //console.log("sport-filter: ", item);
      setTypeSelected(item);
    }

    filtersToSet[type] = item.value;
    setFilters(filtersToSet);
  };

  const checkApplyDisable = () => {
    let disabled = false;

    if (dataSet === "marketscategories") {
      return disabled;
    }

    if (Object.keys(filters).length < 2) {
      if (dataSet !== "regions") {
        disabled = true;
      } else {
        if (Object.keys(filters).length == 1) disabled = false;
        else disabled = true;
      }
    } else {
      for (let index = 0; index < Object.keys(filters).length; index++) {
        const key = Object.keys(filters)[index];
        //("key forLoop: ", key);
        const filter = filters[key];
        if (typeof filter == "number") {
          if (filter == 0) {
            disabled = true;
            break;
          } else {
            continue;
          }
        }
        if (typeof filter == "object") {
          if (filter.length == 0) {
            disabled = true;
            break;
          } else {
            continue;
          }
        }
        if (typeof filter == "string") {
          if (filter == "") {
            disabled = true;
            break;
          } else {
            continue;
          }
        }
      }
    }

    return disabled;
  };

  const handleApply = async () => {
    let user_id = null;
    let provider_id = null;
    let type_id = undefined;
    if (userData) user_id = userData.id;
    if (providerSelected) provider_id = providerSelected.value;
    if (typeSelected) type_id = typeSelected.value;

    if ((!user_id || !provider_id) && dataSet !== "marketscategories") return;
    let sportsList = [];
    let sportNamesList = [];

    if (dataSet !== "regions" && dataSet !== "marketscategories") {
      for (let index = 0; index < sportSelected.length; index++) {
        const sport = sportSelected[index];
        sportsList.push(sport.value);
        sportNamesList.push(sport.label);
      }
    } else {
      sportsList = undefined;
      sportNamesList = null;
    }

    if (sportNamesList && sportNamesList.indexOf("All") > -1) sportNamesList = ["All"];

    setApplyOn(true);
    let postDataEndpoint = `/api/v1/entities/available${dataSet}`;
    let response = await postData(
      postDataEndpoint,
      { user_id: user_id, provider_id: provider_id, sport_id: sportsList, type_id: type_id },
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      if (response.data.length > 0) {
        let data = response.data;
        let filename = sportNamesList
          ? `${providerSelected.label}_${sportNamesList.join("_")}`
          : `${providerSelected ? providerSelected.label : ""}`;
        filename = type_id ? filename + "_" + typeSelected.label : filename;
        filename = filename + "_" + dataSet;

        // sportNamesList
        //     ? `${providerSelected.label}_${sportNamesList.join("_")}_${dataSet}`
        //     : `${providerSelected ? providerSelected.label : ""}_${dataSet}`,
        const dataToCSV = {
          data: data,
          filename: filename,
          delimiter: ";",
        };
        csvDownload(dataToCSV);
        setApiresponse("success");
        setApiResponseMsg(`The list of ${dataSet}, downloaded successfully`);
        setNotification(true);
      } else {
        setApiresponse("error");
        setApiResponseMsg(
          `No ${dataSet} found for the selected criteria. Please try again using new criteria.`
        );
        setNotification(true);
      }
    } else {
      setApiresponse("error");
      setApiResponseMsg("Something goes wrong try again please!");
      setNotification(true);
    }
  };

  const handleReset = () => {
    setSportSelected(null);
    setProviderSelected(null);
    setTypeSelected(null);
    setFilters([]);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  const handleAccordion = () => {
    setFiltersOpen((prev) => !prev);
  };

  useEffect(() => {
    if (forceToggle != undefined) setFiltersOpen(forceToggle);
    setApplyOn(false);
    //console.log("forceToogle: ", forceToggle);
  }, [forceToggle]);

  //   useEffect(() => {
  //     fetchProviders();
  //   }, []);

  useEffect(() => {
    if (dataSet) {
      handleReset();
      fetchProviders();
    }
  }, [dataSet]);

  useEffect(() => {
    return () => {
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <SoftBox my={3} sx={{ marginBottom: "0px" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12} sx={{ marginLeft: "0px !important" }}>
            <SoftBox mb={3}>
              <Grid container spacing={0}>
                <Card style={{ width: "100%", overflow: "visible" }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px", height: "100%" }}>
                    <div>
                      <Accordion
                        expanded={filtersOpen}
                        onChange={handleAccordion}
                        sx={{ boxShadow: "none", borderRadius: "20px !important" }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="settlement-filters-content"
                          id="settlement-filters-header"
                        >
                          <Typography variant="h5">Export - {dataSet}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={3} className="platform-filter-group"></Grid>
                          <Grid container spacing={3} className="fixture-filter-group">
                            <Grid
                              item
                              xs={4}
                              md={4}
                              style={{
                                display: dataSet != "marketscategories" ? "" : "none",
                              }}
                            >
                              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                <SoftTypography
                                  component="label"
                                  variant="caption"
                                  fontWeight="bold"
                                  textTransform="capitalize"
                                >
                                  Providers
                                </SoftTypography>
                                <HtmlTooltip
                                  placement="top"
                                  title={
                                    <React.Fragment>
                                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                                        Select the Provider you want to list the Available Sports
                                        from.
                                      </div>
                                    </React.Fragment>
                                  }
                                >
                                  <Icon className="info-icon-filters" title="">
                                    info_outlined
                                  </Icon>
                                </HtmlTooltip>
                              </SoftBox>
                              <SoftSelect
                                value={providerSelected}
                                id="provider-filter"
                                size="small"
                                options={providers}
                                isMulti={false}
                                onChange={(item) =>
                                  handleSelectionValueChange(item, "provider-filter")
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              md={4}
                              style={{
                                display:
                                  dataSet != "regions" && dataSet != "marketscategories"
                                    ? ""
                                    : "none",
                              }}
                            >
                              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                <SoftTypography
                                  component="label"
                                  variant="caption"
                                  fontWeight="bold"
                                  textTransform="capitalize"
                                >
                                  Sports
                                </SoftTypography>
                                <HtmlTooltip
                                  placement="top"
                                  title={
                                    <React.Fragment>
                                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                                        Select the Sports you want to list the {dataSet} from.
                                      </div>
                                    </React.Fragment>
                                  }
                                >
                                  <Icon className="info-icon-filters" title="">
                                    info_outlined
                                  </Icon>
                                </HtmlTooltip>
                              </SoftBox>
                              <SoftSelect
                                value={sportSelected}
                                id="sport-filter-export"
                                size="small"
                                options={sports}
                                isMulti={true}
                                isDisabled={providerSelected ? false : true}
                                className={providerSelected ? "" : "disabled-form-item"}
                                onChange={(item) =>
                                  handleSelectionValueChange(item, "sport-filter")
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              md={4}
                              style={{
                                display: dataSet == "markets" ? "" : "none",
                              }}
                            >
                              <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                                <SoftTypography
                                  component="label"
                                  variant="caption"
                                  fontWeight="bold"
                                  textTransform="capitalize"
                                >
                                  Type
                                </SoftTypography>
                                <HtmlTooltip
                                  placement="top"
                                  title={
                                    <React.Fragment>
                                      <div style={{ display: "flex", flexWrap: "wrap" }}>
                                        Select the Type of the {dataSet} (Inplay/Pregame).
                                      </div>
                                    </React.Fragment>
                                  }
                                >
                                  <Icon className="info-icon-filters" title="">
                                    info_outlined
                                  </Icon>
                                </HtmlTooltip>
                              </SoftBox>
                              <SoftSelect
                                value={typeSelected}
                                id="type-filter-export"
                                size="small"
                                options={types}
                                isMulti={false}
                                isDisabled={sportSelected ? false : true}
                                className={sportSelected ? "" : "disabled-form-item"}
                                onChange={(item) => handleSelectionValueChange(item, "type-filter")}
                              />
                            </Grid>
                          </Grid>

                          <Grid my={2}>
                            <hr style={{ color: "rgb(236, 236, 236)", border: "0.5px solid" }} />
                          </Grid>
                          <Grid container spacing={1} display="flex" alignItems="flex-end">
                            <Grid item style={{ width: "85%" }}></Grid>
                            <Grid item>
                              <SoftButton color="light" onClick={handleReset}>
                                Reset
                              </SoftButton>
                            </Grid>
                            <Grid item>
                              <SoftButton
                                color="dark"
                                onClick={handleApply}
                                disabled={checkApplyDisable()}
                              >
                                Export
                                <LoopIcon
                                  style={{ display: loading && applyOn ? "" : "none" }}
                                  sx={{
                                    animation: "spin 2s linear infinite",
                                    "@keyframes spin": {
                                      "0%": {
                                        transform: "rotate(360deg)",
                                      },
                                      "100%": {
                                        transform: "rotate(0deg)",
                                      },
                                    },
                                  }}
                                />
                              </SoftButton>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  </Grid>
                </Card>
              </Grid>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

ExportManager.propTypes = {
  applyFilters: PropTypes.bool,
  settlementOutcomes: PropTypes.array,
  informParent: PropTypes.func,
  forceToggle: PropTypes.bool,
  loadingData: PropTypes.bool,
  dataSet: PropTypes.string,
};

export default ExportManager;
